/**
 * React Hook Form + Zod thin integration layer.
 *
 * Provides `useZodForm` (pre-wired useForm with Zod resolver) with a
 * `registerPluma` function that returns Pluma-compatible field props.
 *
 * All standard RHF primitives (Controller, useFormContext, FormProvider)
 * are re-exported for convenience.
 */

import { zodResolver } from '@hookform/resolvers/zod';
import { get } from 'lodash-es';
import {
  useForm,
  useFormContext,
  useController,
  useWatch,
  Controller,
  FormProvider as RHFFormProvider,
} from 'react-hook-form';
import { createContext, use, type ReactNode } from 'react';
import type { ComponentProps, ElementType, RefCallback } from 'react';
import type { FieldError } from 'react-hook-form';
import type { UseFormProps, UseFormReturn, FieldValues, FieldPath, FieldPathValue } from 'react-hook-form';

import type { z } from '@customerio/hydra-validation';

// ---------------------------------------------------------------------------
// Pluma field props — what registerPluma() returns
// ---------------------------------------------------------------------------

export type BaseFormProps<TInputType extends FieldValues, TOutputType extends FieldValues> = {
  onSubmit: (data: TOutputType, formContext: UseFormReturn<TInputType, unknown, TOutputType>) => Promise<void>;
  defaultValues: TInputType;
};

export interface PlumaFieldProps<TValue> {
  /** Pluma `onInput` handler — fires on every keystroke for text inputs. */
  onInput: (event: { target: { value: string } }) => void;
  /** Pluma `onBlur` handler. */
  onBlur: () => void;
  /** Current field value, typed to match the schema field. */
  value: TValue;
  /** Field name. */
  name: string;
  /** Ref for RHF focus management (shouldFocusError). */
  ref: RefCallback<HTMLElement>;
  /** First error message, if any. */
  error: string | undefined;
}

// ---------------------------------------------------------------------------
// useZodForm — pre-configured useForm with Zod resolver + registerPluma
// ---------------------------------------------------------------------------

type UseZodFormProps<TFieldValues extends FieldValues> = Omit<UseFormProps<TFieldValues>, 'resolver'> & {
  // oxlint-disable-next-line typescript/no-explicit-any -- Zod v4 internals require `any` for the second type param
  schema: z.ZodType<TFieldValues, any>;
};

export type UseZodFormReturn<TFieldValues extends FieldValues> = UseFormReturn<TFieldValues> & {
  registerPluma: <TName extends FieldPath<TFieldValues>>(
    name: TName,
  ) => PlumaFieldProps<FieldPathValue<TFieldValues, TName>>;
};

/**
 * A thin wrapper around `useForm` that wires up the Zod resolver and adds `registerPluma`.
 *
 * `registerPluma` works like RHF's `register()` but returns Pluma-compatible props
 * (onInput, value, error) instead of React-compatible ones (onChange, ref).
 *
 * RHF's `register()` doesn't work with Pluma because:
 * 1. Pluma components use `onInput` for keystrokes (not React's `onChange`)
 * 2. Pluma's `useImperativeHandle` ref chain breaks RHF's native event listener attachment
 *
 * ```tsx
 * const form = useZodForm({ schema, defaultValues: { email: '' } });
 *
 * <TextField {...form.registerPluma('email')} label="Email" />
 * ```
 */
export function useZodForm<TFieldValues extends FieldValues>({
  schema,
  ...rest
}: UseZodFormProps<TFieldValues>): UseZodFormReturn<TFieldValues> {
  const form = useForm<TFieldValues>({
    ...rest,
    resolver: zodResolver(schema),
  });

  // Attach registerPluma directly to the form object to keep referential stability.
  // Spreading ({ ...form, registerPluma }) would create a new object every render,
  // making `form` unsafe in useEffect dependency arrays.
  const formWithPluma =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    form as UseZodFormReturn<TFieldValues>;
  formWithPluma.registerPluma = <TName extends FieldPath<TFieldValues>>(
    name: TName,
  ): PlumaFieldProps<FieldPathValue<TFieldValues, TName>> => {
    const value = form.watch(name);
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    const error = get(form.formState.errors, name) as FieldError | undefined;

    return {
      value,
      name,
      ref: (element) => {
        form.register(name).ref(element);
      },
      onInput: (event) => {
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        void form.setValue(name, event.target.value as FieldPathValue<TFieldValues, TName>, {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        });
      },
      onBlur: () => {
        // Mark field as touched (for mode: 'onTouched' and touchedFields)
        void form.setValue(name, form.getValues(name), { shouldTouch: true });
        void form.trigger(name);
      },
      // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
      error: (error?.message as string) ?? undefined,
    };
  };

  return formWithPluma;
}

// ---------------------------------------------------------------------------
// FormProvider — wraps RHF's FormProvider and passes registerPluma via context
// ---------------------------------------------------------------------------

// oxlint-disable-next-line typescript/no-explicit-any -- context needs to accept any form
const RegisterPlumaContext = createContext<UseZodFormReturn<any>['registerPluma'] | null>(null);

/**
 * FormProvider that passes both RHF form methods and registerPluma to children.
 * Use with useZodForm's return value.
 */
export function FormProvider<TFieldValues extends FieldValues>({
  children,
  ...form
}: UseZodFormReturn<TFieldValues> & { children: ReactNode }) {
  return (
    <RHFFormProvider {...form}>
      <RegisterPlumaContext.Provider value={form.registerPluma}>{children}</RegisterPlumaContext.Provider>
    </RHFFormProvider>
  );
}

/**
 * Access the form context including registerPluma in child components.
 */
export function usePlumaFormContext<TFieldValues extends FieldValues = FieldValues>() {
  const rhfContext = useFormContext<TFieldValues>();
  const registerPluma = use(RegisterPlumaContext);

  if (registerPluma == null) {
    throw new Error('usePlumaFormContext must be used within a FormProvider from @/components/form');
  }

  return {
    ...rhfContext,
    registerPluma: registerPluma,
  };
}

// ---------------------------------------------------------------------------
// plumaFieldProps — standalone version of registerPluma for non-Zod forms
// ---------------------------------------------------------------------------

/**
 * Shared Pluma write-path handlers (onInput/onBlur) for an RHF field. Used by
 * both `plumaFieldProps` and `PlumaField` so the bridging lives in one place —
 * the only difference between the two is where the value/error are *read* from
 * (host `form.watch` vs leaf `useController`).
 */
function plumaInputHandlers<TFieldValues extends FieldValues>(
  form: UseFormReturn<TFieldValues>,
  name: FieldPath<TFieldValues>,
) {
  return {
    onInput: (event: { target: { value: string } }) => {
      // The DOM value is a string; the resolver coerces (e.g. number ports).
      form.setValue(
        name,
        // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
        event.target.value as FieldPathValue<TFieldValues, typeof name>,
        {
          shouldValidate: true,
          shouldDirty: true,
          shouldTouch: true,
        },
      );
    },
    onBlur: () => {
      form.setValue(name, form.getValues(name), { shouldTouch: true });
      void form.trigger(name);
    },
  };
}

/**
 * Returns Pluma-compatible field props from any RHF form instance.
 * Use this when you can't use `useZodForm` (e.g. custom resolver).
 *
 * Uses `form.watch` internally so the calling component re-renders when the
 * field value changes — even when the form was created in a parent component
 * or provided via context. For large forms where this host-wide re-render is
 * expensive, prefer `PlumaField` (per-field subscription via `useController`).
 *
 * Note: unlike `registerPluma`, this does not pass a `ref` to RHF because
 * `form.register()` attaches native onChange listeners that conflict with
 * our onInput approach for `type="number"` inputs. This means `shouldFocusError`
 * won't auto-focus invalid fields.
 *
 * ```tsx
 * const form = useForm({ resolver: myCustomResolver, defaultValues: { name: '' } });
 * <TextField {...plumaFieldProps(form, 'name')} label="Name" />
 * ```
 */
// oxlint-disable-next-line typescript/no-explicit-any -- needs to work with any form shape
export function plumaFieldProps(form: UseFormReturn<any>, name: string) {
  const value = form.watch(name);
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  const error = get(form.formState.errors, name) as FieldError | undefined;

  return {
    value: value != null ? String(value) : '',
    name,
    ...plumaInputHandlers(form, name),
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    error: (error?.message as string) ?? undefined,
  };
}

// ---------------------------------------------------------------------------
// PlumaField — re-render-isolated field wrapper
// ---------------------------------------------------------------------------

type PlumaFieldComponentProps<TFieldValues extends FieldValues, TAs extends ElementType> = {
  /** The RHF form instance (from useForm/useZodForm). */
  form: UseFormReturn<TFieldValues>;
  /**
   * Field path, e.g. "config.host". Typed as `string` (not `FieldPath`) to stay
   * a drop-in for `plumaFieldProps`, which also accepted any string path.
   */
  name: string;
  /** The Pluma field component to render, e.g. TextField or TextArea. */
  as: TAs;
  // Omit the props PlumaField controls, plus the native `form`/`name`/`as`
  // HTML attributes that polymorphic Pluma inputs expose (they collide with
  // our own `form`/`name`/`as` props otherwise).
} & Omit<ComponentProps<TAs>, 'value' | 'onInput' | 'onBlur' | 'error' | 'name' | 'form' | 'as'>;

/**
 * Renders a Pluma field bound to an RHF form, with the same onInput/onBlur
 * bridging as `plumaFieldProps` — but the value/error subscription lives in
 * THIS leaf via `useController`, not at the host via `form.watch`. So typing
 * re-renders only this field instead of the whole form. Use it in large,
 * multi-field forms where host-wide re-renders are expensive.
 *
 * Like `plumaFieldProps`, it intentionally does not attach RHF's `register`
 * ref: that wires a native onChange listener which conflicts with Pluma's
 * onInput for `type="number"` inputs (see plumaFieldProps note above). We read
 * the value/error from `useController` but keep `setValue` as the write path.
 *
 * ```tsx
 * <PlumaField form={form} name="config.host" as={TextField} label="Host" />
 * ```
 */
export function PlumaField<TFieldValues extends FieldValues, TAs extends ElementType>({
  form,
  name,
  as: As,
  ...rest
}: PlumaFieldComponentProps<TFieldValues, TAs>) {
  // Caller-supplied path is trusted (same contract as plumaFieldProps).
  const fieldName =
    // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
    name as FieldPath<TFieldValues>;
  // useController subscribes THIS leaf to the field's value + error, so typing
  // re-renders only this field rather than the whole form (vs form.watch).
  const { field, fieldState } = useController({ control: form.control, name: fieldName });
  const value = field.value;

  const bridged = {
    name,
    value: value != null ? String(value) : '',
    ...plumaInputHandlers(form, fieldName),
    error: fieldState.error?.message,
  };

  // Widen to ElementType for the spread: the bridged props are validated by the
  // PlumaFieldComponentProps<TAs> signature at the call site, but TS can't relate
  // them to a generic component's LibraryManagedAttributes here.
  const Component: ElementType = As;
  return <Component {...rest} {...bridged} />;
}

// Re-export RHF primitives
// fallow-ignore-next-line unused-export
export { useForm, useFormContext, useController, useWatch, Controller };
export type { UseFormReturn } from 'react-hook-form';
